<template>
  <section class="latest-news">
    <div class="container">
      <!-- Título -->
      <h2 class="latest-news__title d-flex justify-content-center">
        {{ titles.heading }}
      </h2>

      <!-- Cuerpo: columnas en desktop, stack en mobile -->
      <div
        class="latest-news__body d-flex"
        :class="isMobile ? 'flex-column align-items-stretch' : 'flex-row align-items-stretch'"
      >
        <!-- Artículo destacado -->
        <article class="featured-article">
          <div class="featured-article__meta">
            <time>{{ formatDate(mainArticle.date) }}</time>
            <span class="tag">{{ mainArticle.tags[0] || '' }}</span>
          </div>

          <h3 class="featured-article__heading">
            {{ mainArticle.displayTitle }}
          </h3>

          <div v-if="mainImage" class="featured-article__image">
            <img
              :src="mainImage"
              :alt="mainArticle.displayTitle"
              class="featured-article__img"
            />
          </div>

          <RouterLink
            v-if="mainArticle.slug"
            @click="goToServicio()"
            :to="{ name: 'BlogPost', params: { slug: mainArticle.slug } }"
            class="featured-article__read-more"
          >
            <span class="line"></span>
            {{ titles.readMore }}
          </RouterLink>
        </article>

        <!-- Tres artículos secundarios -->
        <div class="other-articles">
          <article
            v-for="art in otherArticles"
            :key="art.id"
            class="other-article"
          >
            <div class="other-article__meta">
              <time>{{ formatDate(art.date) }}</time>
              <span class="tag">{{ art.tags[0] || '' }}</span>
            </div>

            <h4 class="other-article__heading">
              {{ art.displayTitle }}
            </h4>

            <RouterLink
              v-if="art.slug"
              @click="goToServicio()"
              :to="{ name: 'BlogPost', params: { slug: art.slug } }"
              class="other-article__read-more"
            >
              <span class="line small"></span>
              {{ titles.readMore }}
            </RouterLink>
          </article>
        </div>
      </div>

      <div
        class="latest-news__footer d-flex justify-content-center align-items-center"
      >
        <div class="last-is-section-btn">
          <RouterLink to="/blog" class="btn-all" @click="goToServicio()">
            {{ titles.viewAll }}
          </RouterLink>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { database, ref, get, query, orderByChild } from '@/firebase.js'

export default {
  name: 'LatestNews',
  props: {
    selectedLang: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      sortedArticles: [],
      isMobile: false,
      titles: {
        heading: {
          es: 'Últimos artículos',
          en: 'Latest Articles',
          de: 'Neueste Artikel'
        }[this.selectedLang] || 'Últimos artículos',
        readMore: {
          es: 'Leer más',
          en: 'Read more',
          de: 'Mehr lesen'
        }[this.selectedLang] || 'Leer más',
        viewAll: {
          es: 'Leer todos los artículos',
          en: 'View all articles',
          de: 'Alle Artikel anzeigen'
        }[this.selectedLang] || 'Leer todos los artículos'
      }
    }
  },
  computed: {
    mainArticle () {
      const art = this.sortedArticles[0] || {
        date: '',
        tags: [],
        title: {},
        slug: '',
        content: {}
      }
      return {
        ...art,
        tags: Array.isArray(art.tags) ? art.tags : [],
        displayTitle: art.title
          ? art.title[this.selectedLang] || art.title.es || ''
          : ''
      }
    },
    otherArticles () {
      return this.sortedArticles.slice(1, 4).map(art => ({
        ...art,
        tags: Array.isArray(art.tags) ? art.tags : [],
        displayTitle: art.title
          ? art.title[this.selectedLang] || art.title.es || ''
          : ''
      }))
    },
    mainImage () {
      return this.getArticleImage(this.mainArticle)
    }
  },
  async mounted () {
    await this.fetchArticles()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    async fetchArticles () {
      try {
        const articlesRef = ref(database, 'blogArticles')
        const articlesQuery = query(articlesRef, orderByChild('createdAt'))
        const snapshot = await get(articlesQuery)

        if (snapshot.exists()) {
          const data = snapshot.val()

          const allArticles = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          }))

          this.sortedArticles = allArticles
            .filter(a => (a.language || '').includes(this.selectedLang))
            .sort(
              (a, b) =>
                new Date(b.createdAt || b.date) -
                new Date(a.createdAt || a.date)
            )
        } else {
          this.sortedArticles = []
        }
      } catch (error) {
        console.error('Error al cargar artículos en LatestNews:', error)
        this.sortedArticles = []
      }
    },
    getArticleImage (article) {
      const contentByLang = article.content?.[this.selectedLang] || []
      const firstImg = contentByLang.find(c => c.type === 'image')

      return firstImg
        ? firstImg.url
        : article.headerImage ||
            'https://www.edsrobotics.com/wp-content/uploads/2020/05/industria-4.0-900x480.jpg'
    },
    checkMobile () {
      this.isMobile = window.innerWidth <= 768
    },
    goToServicio () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString(this.selectedLang, {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.latest-news {
  padding: 2rem 1rem;
  background-color: #f0f6f8;
}
.container {
  max-width: 1200px;
  margin: 0 auto;
}
.latest-news__title {
  font-family: 'Permanent Marker', cursive;
  font-size: 3rem;
  text-align: right;
  margin-bottom: 1.5rem;
}
.latest-news__body {
  display: flex;
  gap: 1.5rem;
}

/* --- FEATURED ARTICLE --- */
.featured-article {
  flex: 2;
  border: 2px solid #000;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.featured-article__meta {
  font-family: monospace;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 0.5rem;
}
.featured-article__heading {
  font-family: monospace;
  font-size: 1.25rem;
  line-height: 1.3;
  margin-bottom: 1rem;
}

/* Contenedor de imagen con altura fija y overflow oculto */
.featured-article__image {
  width: 100%;
  max-height: 220px;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1rem;
}

/* Imagen recortada tipo card, como en el grid */
.featured-article__img {
  width: 100%;
  height: 100%;
  max-height: 220px;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}

.featured-article__img:hover {
  transform: scale(1.03);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.featured-article__read-more {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  font-family: monospace;
  font-size: 0.9rem;
  text-transform: uppercase;
  font-weight: bold;
  color: currentColor;
}
.featured-article__read-more .line {
  width: 40px;
  height: 1px;
  background: #000;
  margin-right: 0.5rem;
}

/* --- OTHER ARTICLES --- */
.other-articles {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.other-article {
  border: 2px solid #000;
  padding: 1.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #fff;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
}
.other-article:hover {
  transform: scale(1.03);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}
.other-article__meta {
  font-family: monospace;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 0.5rem;
}
.other-article__heading {
  font-family: monospace;
  font-size: 1rem;
  line-height: 1.3;
  margin-bottom: 1rem;
}
.other-article__read-more {
  display: inline-flex;
  align-items: center;
  font-family: monospace;
  font-size: 0.85rem;
  text-transform: uppercase;
  font-weight: bold;
  color: currentColor;
}
.other-article__read-more .line.small {
  width: 30px;
  height: 1px;
  background: #000;
  margin-right: 0.5rem;
}

/* FOOTER */
.latest-news__footer {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

.btn-all {
  font-family: 'Permanent Marker', cursive;
  text-transform: uppercase;
  background: #000;
  color: #fff;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  text-align: justify;
  border-radius: 999px;
  border: none;
  width: 100%;
  max-width: 400px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease,
    box-shadow 0.3s ease;
}
.btn-all:hover {
  background-color: #2c74b8;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  color: #ffffff;
}

.last-is-section-btn{
  text-align: center !important;
}
/* RESPONSIVE */
@media (max-width: 768px) {
  .latest-news__body {
    flex-direction: column;
  }

  .latest-news__title {
    font-size: 2.3rem;
    text-align: center;
  }

  .featured-article__image,
  .featured-article__img {
    max-height: 200px;
  }
}
</style>
